//#region IMPORT
import React from 'react';
/** Components Import */
import {
  Stack,
  Box,
  Typography,
  Accordion,
  AccordionDetails,
  Chip,
} from '@mui/material';
import MuiAccordionSummary, {
  accordionSummaryClasses,
} from '@mui/material/AccordionSummary';
import { styled } from '@mui/material/styles';
import { Button } from '../../../Components/MUIs';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
/** Icons Import */
import dayjs from 'dayjs';
import dateIcon from '../../../Assets/Images/dateIcon.svg';
import PinchZoom from '../../../Components/Ui/PinchZoom';
import ParticipantCard from '../ui/ParticipantCard';
import Section from '../ui/Section';
import SignBottomSheet from '../../../Components/Ui/SignBottomSheet';
import { useSelector } from 'react-redux';
//#endregion

const AccordionSummary = styled((props) => (
  <MuiAccordionSummary expandIcon={<ExpandMoreIcon />} {...props} />
))(({ theme }) => ({
  mb: '8px',
  borderRadius: '12px',
  backgroundColor: '#f8f9fa',
  border: '1px solid #e9ecef',
  '&:hover': {
    backgroundColor: '#e9ecef',
    borderColor: '#2DB400',
  },
  [`& .${accordionSummaryClasses.expandIconWrapper}.${accordionSummaryClasses.expanded}`]:
    {
      transform: 'rotate(180deg)',
      color: '#2DB400',
    },
  [`& .${accordionSummaryClasses.content}`]: {
    marginRight: theme.spacing(1),
    alignItems: 'center',
  },
}));

const Divider = () => <Box sx={{ p: 1, backgroundColor: '#F3F7F8' }} />;

function M_MET_BASIC_01(props) {
  const bottomSheetState = useSelector((state) => state.bottomSheet);
  const isContractorMeeting = props.id?.indexOf('C') === 0;
  return (
    <Stack>
      {/* 회의 제목 섹션 */}
      <Section title="회의 제목">
        <Typography
          sx={{ fontSize: '1.1rem', color: '#2c3e50', lineHeight: 1.4 }}
        >
          {props.alarmData[0]?.proceedingsTitle || '회의 제목 없음'}
        </Typography>
      </Section>

      <Divider />

      {/* 회의 일시 섹션 */}
      <Section title="회의 일시">
        <Stack direction="row" sx={{ mb: 2 }}>
          <Box sx={{ position: 'relative', width: 48, height: 48 }}>
            <img src={dateIcon} alt="calendar" width="100%" height="100%" />
            <Typography
              sx={{
                position: 'absolute',
                top: '65%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                fontSize: 18,
                fontWeight: 'bold',
                color: '#333',
              }}
            >
              {dayjs(props.alarmData[0]?.proceedingsDate).date()}
            </Typography>
          </Box>
          <Box>
            <Typography sx={{ px: 2 }}>
              {
                props.FormatDate(props.alarmData[0]?.proceedingsDate)
                  .formattedDate
              }
            </Typography>
            <Typography sx={{ px: 2, color: 'text.secondary' }}>
              {
                props.FormatDate(
                  props.alarmData[0]?.proceedingsDate,
                  props.alarmData[0]?.proceedingsStartTime,
                  props.alarmData[0]?.proceedingsEndTime
                ).formattedTime
              }
            </Typography>
          </Box>
        </Stack>
      </Section>

      <Divider />

      {/* 회의 장소 섹션 */}
      <Section title="회의 장소">
        <Typography
          sx={{ fontSize: '1rem', color: '#495057', lineHeight: 1.4 }}
        >
          {props.alarmData[0]?.proceedingsLocation || '회의 장소 없음'}
        </Typography>
      </Section>

      <Divider />

      {/* 참석자 섹션 */}
      <Section
        title="참석자"
        headerChildren={
          <span
            style={{
              fontWeight: 'normal',
              color: 'rgba(0,0,0,0.6)',
              fontSize: 14,
            }}
          >
            {(() => {
              const uniqueAlarmData = props.alarmData.filter(
                (item, index, self) => {
                  if (item.id) {
                    return index === self.findIndex((t) => t.id === item.id);
                  } else {
                    return (
                      index ===
                      self.findIndex((t) => !t.id && t.name === item.name)
                    );
                  }
                }
              );

              const totalCount = uniqueAlarmData.length;

              return `(총원 ${totalCount}명)`;
            })()}
          </span>
        }
      >
        <Stack gap={1}>
          <ParticipantCard
            title={isContractorMeeting ? '도급사' : '사용자위원'}
            icon="🏢"
            color="#2DB400"
            participants={props.ParseParticipant(props.GetAttendees('0'))}
            emptyMessage={
              isContractorMeeting
                ? '도급사 참석자 없음'
                : '사용자위원 참석자 없음'
            }
          />

          <ParticipantCard
            title={isContractorMeeting ? '수급사' : '근로자위원'}
            icon="🏗️"
            color="#007bff"
            participants={props.ParseParticipant(props.GetAttendees('1'))}
            emptyMessage={
              isContractorMeeting
                ? '수급사 참석자 없음'
                : '근로자위원 참석자 없음'
            }
          />
        </Stack>
      </Section>

      <Divider />

      {/* 회의 안건 섹션 */}
      <Section
        title="회의 안건"
        headerChildren={
          <Chip
            label={`${
              props
                .ParseMeeting(props.alarmData)
                .filter((item) => item.agendaName).length
            }건`}
            size="small"
            sx={{
              backgroundColor: '#2DB400',
              color: 'white',
              fontWeight: 'bold',
              fontSize: '0.8rem',
              height: 24,
            }}
          />
        }
      >
        {/* 안건 목록 */}
        {props.ParseMeeting(props.alarmData).some((item) => item.agendaName) ? (
          <Stack spacing={1}>
            {props
              .ParseMeeting(props.alarmData)
              .filter((item) => item.agendaName)
              .map((item, index) => (
                <Accordion
                  key={index}
                  sx={{
                    boxShadow: 'none',
                    borderRadius: '12px !important',
                    backgroundColor: '#f8f9fa',
                    border: '1px solid #e9ecef',
                    '&:before': { display: 'none' },
                    '&.Mui-expanded': {
                      backgroundColor: '#f8f9fa',
                      borderColor: '#2DB400',
                      borderRadius: '12px !important',
                      marginBottom: '8px !important',
                    },
                    '& .MuiAccordionSummary-root': {
                      border: 'none',
                      borderRadius: '12px',
                      '&:before': { display: 'none' },
                      '&.Mui-expanded': {
                        borderRadius: '12px 12px 0 0',
                      },
                    },
                    '& .MuiAccordionDetails-root': {
                      border: 'none',
                      borderRadius: '0 0 12px 12px',
                      '&:before': { display: 'none' },
                    },
                  }}
                >
                  <AccordionSummary
                    sx={{
                      p: 1.5,
                      '& .MuiAccordionSummary-content': {
                        margin: '0px',
                      },
                      '&:hover': {
                        backgroundColor: 'transparent',
                      },
                      '&:focus': {
                        backgroundColor: 'transparent',
                      },
                      '&:focus-visible': {
                        backgroundColor: 'transparent',
                        outline: 'none',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        width: '100%',
                      }}
                    >
                      {/* 안건 번호 */}
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 28,
                          height: 28,
                          borderRadius: '50%',
                          backgroundColor: '#2DB400',
                          color: 'white',
                          fontWeight: 'bold',
                          fontSize: '0.9rem',
                          mr: 2,
                          flexShrink: 0,
                        }}
                      >
                        {index + 1}
                      </Box>

                      {/* 안건 제목 */}
                      <Typography
                        sx={{
                          fontWeight: 'bold',
                          color: '#2c3e50',
                          flex: 1,
                          fontSize: '1rem',
                          lineHeight: 1.4,
                        }}
                      >
                        {item.agendaName}
                      </Typography>
                    </Box>
                  </AccordionSummary>

                  <AccordionDetails
                    sx={{
                      pt: 0,
                      pb: 2,
                      px: 2,
                      backgroundColor: 'transparent',
                    }}
                  >
                    <Box
                      sx={{
                        backgroundColor: '#ffffff',
                        p: 2,
                        borderRadius: '8px',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                      }}
                    >
                      <Typography
                        sx={{
                          color: '#495057',
                          lineHeight: 1.6,
                          fontSize: '0.95rem',
                          whiteSpace: 'pre-wrap',
                        }}
                      >
                        {item.resultDiscussion}
                      </Typography>
                    </Box>
                  </AccordionDetails>
                </Accordion>
              ))}
          </Stack>
        ) : (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '120px',
              color: '#888',
              fontSize: '1rem',
              background: '#f8f9fa',
              borderRadius: '8px',
              border: '1px solid #e9ecef',
              mt: 1,
              mb: 2,
            }}
          >
            등록된 안건이 없습니다
          </Box>
        )}
      </Section>

      <Divider />

      {/* 진행 사진 섹션 */}
      <Section title="진행 사진">
        {props.imgRef.current ? (
          <PinchZoom
            contents={
              <img
                src={props.imgRef.current}
                alt="proceedingImage"
                style={{
                  paddingTop: '8px',
                  maxWidth: '100%',
                  objectFit: 'contain',
                }}
              />
            }
          />
        ) : (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '120px',
              color: '#888',
              fontSize: '1rem',
              background: '#f8f9fa',
              borderRadius: '8px',
              border: '1px solid #e9ecef',
              mt: 1,
              mb: 2,
            }}
          >
            등록된 이미지가 없습니다
          </Box>
        )}
        <Stack direction="row" gap={1} sx={{ py: 2 }}>
          {/* 반려 버튼 */}
          {props.alarmData[0]?.ceoCheck && (
            <Button
              name="반려"
              variant="contained"
              fullWidth
              onClick={(e) => props.ReturnRequest()}
              sx={{
                height: '36px',
                borderRadius: '18px',
                backgroundColor: '#D32F2F',
                '&:hover': {
                  backgroundColor: '#B71C1C', // hover 시 배경색
                },
              }}
              disabled={props.alarmData[0]?.ceoCheck === 'Y'}
            />
          )}
          {/* 승인 버튼 */}
          {props.alarmData[0]?.ceoCheck && (
            <Button
              name="승인"
              variant="contained"
              fullWidth
              onClick={() => props.SaveLogic1()}
              sx={{
                height: '36px',
                borderRadius: '18px',
                backgroundColor: '#2DB400',
                '&:hover': {
                  backgroundColor: '#26a300',
                },
              }}
              disabled={props.alarmData[0]?.ceoCheck === 'Y'}
            />
          )}
          {/* 서명하기 버튼 */}
          {!props.alarmData[0]?.ceoCheck && (
              <Button
                name="서명하기"
                variant="contained"
                fullWidth
                onClick={() => {
                  if (props.onOpenSignatureDrawer) {
                    props.onOpenSignatureDrawer();
                  } else {
                    props.ToggleDrawer('signature', true)();
                  }
                }}
                sx={{
                  height: '36px',
                  borderRadius: '18px',
                  backgroundColor: '#2DB400',
                  '&:hover': {
                    backgroundColor: '#26a300',
                  },
                }}
              />
            )}
        </Stack>
      </Section>
      {bottomSheetState.bottom && bottomSheetState.type === 'signature' && (
        <SignBottomSheet
          signMode="registration"
          open={bottomSheetState.bottom}
          onClose={props.ToggleDrawer('signature', false)}
          signatureCanvas={props.signatureCanvas}
          userInfo={props.userInfo}
          signInputs={props.signInputs}
          setSignInputs={props.setSignInputs}
          alarmData={props.alarmData}
          onSave={() => props.SaveLogic1()}
        />
      )}
    </Stack>
  );
}

export default M_MET_BASIC_01;
